import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ResidentBanner from "components/shared/residentBanner/ResidentBanner";
import { DEFAULT_EMPTY, GUEST_TYPES } from "store/store.constants";

export default function ResidentDetailSection({
  residentId,
  residentInfo,
  diningAreas,
  selectedDiningAreaId,
  onDiningAreaChange,
  selectedTableName,
  onTableChange,
  outOfBuilding,
  onOutOfBuildingChange,
  declinedMeal,
  onDeclinedMealChange,
  mealAddons,
  menuInfo,
  showPhoto,
  residentName,
  roomBed,
  guestInfo,
}) {
  const clearLiquidNote =
    "Only Serve fluids and foods that are transparent " +
    "(i.e. apple juice, cranberry juice, grape juice, flavored gelatin, broth and popsicles.) Thicken liquids as ordered.";
  const fullLiquidNote =
    "Only Serve liquids or foods that become liquid at room temperature (i.e. coffee, tea, juice, " +
    "gelatin, gravy, ice cream, sherbet, milk, ice chips, popsicles, strained soups and plain yogurt). Thicken liquids as ordered.";
  const liquidizedNote =
    "PUREED FOOD MUST BE FURTHER LIQUIDIZED. PLEASE REFER TO THE LIQUIDIZED PUREED FOODS RECIPE FOR DIRECTIONS.";

  const [hasBannerPhoto, setHasBannerPhoto] = useState(false);
  const [mealCostPlan, setMealCostPlan] = useState(null);
  const [mealCountPlan, setMealCountPlan] = useState(null);

  useEffect(() => {
    if (residentInfo?.mealPlans?.length) {
      setMealCostPlan(residentInfo.mealPlans.find((plan) => plan.mealPlanId === 1));
      setMealCountPlan(residentInfo.mealPlans.find((plan) => plan.mealPlanId === 2));
    }
  }, [residentInfo]);

  const getMealPlanTypes = (plan) => {
    if (plan?.isBreakfast && plan?.isLunch && plan?.isDinner) return "All Meals";
    
    const types = [];
    if (plan?.isBreakfast) types.push("Breakfast");
    if (plan?.isLunch) types.push("Lunch");
    if (plan?.isDinner) types.push("Dinner");
    return types.join(", ");
  };

  const renderMealPlanRow = ({
    title,
    subtitle,
    primaryValue,
    primaryLabel,
    secondaryValue,
    secondaryLabel,
    isSecondaryNegative,
  }) => (
    <Box sx={{ display: "flex", flexWrap: "nowrap", alignItems: "center", width: "100%" }}>
      <Box sx={{ width: "15rem" }}>
        <Typography variant="body1" component="div" fontWeight="bold" sx={{ margin: 0, lineHeight: 1.4 }}>
          {title}
        </Typography>
        <Typography variant="caption" component="div" color="text.secondary" sx={{ margin: 0, lineHeight: 1.4 }}>
          {subtitle}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: "center", alignItems: "center" }}>
        <Typography variant="body1" component="div" fontWeight="bold" sx={{ margin: 0, lineHeight: 1.4 }}>
          {primaryValue}
        </Typography>
        <Typography variant="caption" component="div" color="text.secondary" sx={{ margin: 0, lineHeight: 1.4 }}>
          {primaryLabel}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: "center" }}>
        <Typography variant="body1" component="div" fontWeight="bold" 
          sx={{ 
            margin: 0, 
            lineHeight: 1.4, 
            color: isSecondaryNegative ? "error.main" : "#000" 
          }}
        >
          {secondaryValue}
        </Typography>
        <Typography variant="caption" component="div" color="text.secondary" 
          sx={{ 
            margin: 0, 
            lineHeight: 1.4, 
          }}
        >
          {secondaryLabel}
        </Typography>
      </Box>
    </Box>
  );

  const checkForDietNameSeparator = () => {
    return (
      residentInfo?.dietName !== "N/A" &&
      residentInfo?.dietName &&
      !DEFAULT_EMPTY.includes(residentInfo?.dietTextureName) &&
      residentInfo?.dietTextureName !== "Regular" &&
      residentInfo?.dietTextureName
    );
  };

  const checkForDietOtherSeparator = () => {
    return (
      (residentInfo?.dietName !== "N/A" &&
        residentInfo?.dietName &&
        residentInfo?.dietTextureName !== "N/A" &&
        residentInfo?.dietTextureName &&
        residentInfo?.dietOther !== "N/A" &&
        residentInfo?.dietOther) ||
      (residentInfo?.dietName && !residentInfo?.dietTextureName && residentInfo?.dietOther) ||
      (!residentInfo?.dietName && residentInfo?.dietTextureName && residentInfo?.dietOther)
    );
  };

  const renderDiningAreas = () => {
    const areas = [];
    if (diningAreas?.length) {
      diningAreas.forEach((area) => {
        areas.push(
          <MenuItem key={area.id} value={area.id}>
            <span className="general-text" style={{ fontWeight: "bold" }}>
              {area.name}
            </span>
          </MenuItem>
        );
      });
    }
    return areas;
  };

  const renderTables = () => {
    const tables = [];
    if (selectedDiningAreaId) {
      const diningArea = diningAreas.find((area) => area.id === selectedDiningAreaId);
      if (diningArea && diningArea?.tables?.length) {
        diningArea.tables.forEach((table) => {
          tables.push(
            <MenuItem key={table.id} value={table.name}>
              <span className="general-text" style={{ fontWeight: "bold" }}>
                {table.name}
              </span>
            </MenuItem>
          );
        });
      } else {
        tables.push(
          <MenuItem key={"N/A"} value={"N/A"}>
            <span className="general-text" style={{ fontWeight: "bold" }}>
              {"N/A"}
            </span>
          </MenuItem>
        );
      }
    }
    return tables;
  };

  return (
    <>
      <ResidentBanner
        residentId={residentId}
        residentName={residentName}
        {...roomBed}
        showPhoto={showPhoto}
        onPhotoVisibilityChange={setHasBannerPhoto}
      />
      <Card className="underCard">
        <CardContent sx={{ display: "flex", flexWrap: "wrap" }}>
          <div
            style={{
              width: "100%",
              padding: ".5rem 0",
              borderBottom: "1px solid lightgray",
            }}>
            <span
              style={{ margin: "0 2rem 0 .875rem" }}
              className="general-text">
              Dining Area
            </span>
            <Select
              sx={{ marginRight: "2rem" }}
              variant="standard"
              value={selectedDiningAreaId}
              onChange={(event) => onDiningAreaChange(event.target.value)}>
              {renderDiningAreas()}
            </Select>

            <span style={{ marginRight: "2rem" }} className="general-text">
              Table #
            </span>
            <Select
              variant="standard"
              value={selectedTableName}
              onChange={(event) => onTableChange(event.target.value)}>
              {renderTables()}
            </Select>
          </div>
          <Table id="info-table">
            <TableBody>
              {residentInfo &&
                ((residentInfo.dietName !== "N/A" && !!residentInfo.dietName) ||
                  (residentInfo.dietTextureName !== "N/A" &&
                    !!residentInfo.dietTextureName) ||
                  (residentInfo.dietOther !== "N/A" &&
                    !!residentInfo.dietOther)) && (
                  <TableRow>
                    <TableCell sx={{ width: "15rem" }}>
                      <span className="general-text">Diet</span>
                    </TableCell>
                    <TableCell
                      id={hasBannerPhoto ? "shortDiet" : ""}
                      className="tableValue">
                      <span className="general-text">
                        {residentInfo &&
                          residentInfo.dietName !== "N/A" &&
                          residentInfo.dietName}
                      </span>
                      <span className="general-text">
                        {checkForDietNameSeparator() && "  |  "}
                      </span>
                      <span className="general-text">
                        {residentInfo.dietTextureName !== "" &&
                          residentInfo.dietTextureName !== "Regular" &&
                          `${residentInfo.dietTextureName}`}
                      </span>
                      <span className="general-text">
                        {checkForDietOtherSeparator() && "  |  "}
                      </span>
                      <span className="general-text">
                        {residentInfo.dietOther !== "" &&
                          `${residentInfo.dietOther}`}
                      </span>{" "}
                    </TableCell>
                  </TableRow>
                )}
              {residentInfo &&
              !DEFAULT_EMPTY.includes(residentInfo.liquidConsistency) && (
                  <TableRow>
                    <TableCell>
                      <span className="general-text">
                        Liquid Consistency
                      </span>
                    </TableCell>
                    <TableCell className="tableValue">
                      <span className="general-text">
                        {residentInfo.liquidConsistency}
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              {residentInfo &&
              !DEFAULT_EMPTY.includes(residentInfo.fluidRestriction) && (
                  <TableRow>
                    <TableCell>
                      <span className="general-text">
                        Fluid Restriction
                      </span>
                    </TableCell>
                    <TableCell className="tableValue">
                      <span className="general-text">
                        {residentInfo.fluidRestriction}
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              {residentInfo &&
              !DEFAULT_EMPTY.includes(residentInfo.dislikes) && (
                <TableRow>
                  <TableCell>
                    <span className="general-text">Dislikes</span>
                  </TableCell>
                  <TableCell className="tableValue">
                    <span className="general-text">
                      {residentInfo.dislikes}
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {residentInfo &&
              !DEFAULT_EMPTY.includes(residentInfo.allergies) && (
                  <TableRow>
                    <TableCell>
                      <span className="general-text">Allergies</span>
                    </TableCell>
                    <TableCell className="tableValue">
                      <span className="general-text">
                        {residentInfo.allergies}
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              {!!(mealAddons && mealAddons.length) && (
                <TableRow>
                  <TableCell>
                    <span className="general-text">Meals AddOn</span>
                  </TableCell>
                  <TableCell className="tableValue">
                    {mealAddons.map((item, idx) => {
                      return (
                        <div className="general-text" key={idx}>
                          {item?.trim()}
                        </div>
                      );
                    })}
                  </TableCell>
                </TableRow>
              )}
              {residentInfo &&
              !DEFAULT_EMPTY.includes(residentInfo.adaptiveEquipment) && (
                  <TableRow>
                    <TableCell>
                      <span className="general-text">
                        Adaptive Equipment
                      </span>
                    </TableCell>
                    <TableCell className="tableValue">
                      <span className="general-text">
                        {residentInfo.adaptiveEquipment}
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              {residentInfo &&
              !DEFAULT_EMPTY.includes(residentInfo.dietPrecautionsNote) && (
                <TableRow>
                  <TableCell>
                    <span className="general-text">
                      Diet Precautions Note
                    </span>
                  </TableCell>
                  <TableCell className="tableValue">
                    <span className="general-text">
                      {residentInfo &&
                        residentInfo.dietPrecautionsNote !== "N/A" &&
                        residentInfo.dietPrecautionsNote}
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {!!(
                menuInfo?.order?.residentId === 0 ||
                menuInfo?.order?.guestTypeId > 0
              ) && (
                <TableRow>
                  <TableCell>
                    <span className="general-text">Guest Type</span>
                  </TableCell>
                  <TableCell className="tableValue">
                    <span className="general-text">
                      {!DEFAULT_EMPTY.includes(
                        guestInfo?.selectedGuestType
                      ) && GUEST_TYPES[guestInfo?.selectedGuestType || 0]}
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {((residentInfo && residentInfo.notes && residentInfo.notes.length > 0) ||
                residentInfo?.isClearLiquid ||
                residentInfo?.isFullLiquid ||
                residentInfo?.isLiquidized) && (
                <TableRow>
                  <TableCell>
                    <span className="general-text">Notes</span>
                  </TableCell>
                  <TableCell className="tableValue">
                    {residentInfo.isClearLiquid && (
                      <div className="general-text">{clearLiquidNote}</div>
                    )}
                    {residentInfo.isFullLiquid && (
                      <div className="general-text">{fullLiquidNote}</div>
                    )}
                    {residentInfo.isLiquidized && (
                      <div className="general-text">{liquidizedNote}</div>
                    )}
                    {residentInfo.notes.map((item, idx) => {
                      return (
                        <div className="general-text" key={idx}>
                          {item}
                        </div>
                      );
                    })}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="orderCheckbox"
                        checked={declinedMeal}
                        onChange={onDeclinedMealChange}
                      />
                    }
                    label={
                      <span className="general-text tableValue">
                        Declined Meal
                      </span>
                    }
                  />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="orderCheckbox"
                        checked={outOfBuilding}
                        onChange={onOutOfBuildingChange}
                      />
                    }
                    label={
                      <span className="general-text tableValue">
                        Out of Building
                      </span>
                    }
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {!!residentInfo?.mealPlans?.length && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                width: "100%",
                minHeight: "4rem",
                backgroundColor: "#f4f8f9",
                marginTop: ".75rem",
                borderRadius: "var(--borderRadius)",
                padding: ".5rem .75rem"
              }}
            >
              <Typography width="12.5rem" color="var(--tealColor)" fontWeight="bold">
                Meal Plan
              </Typography>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: ".5rem" }}>
                {mealCountPlan &&
                  renderMealPlanRow({
                    title: "Meal Count",
                    subtitle: getMealPlanTypes(mealCountPlan),
                    primaryValue:
                      mealCountPlan.mealCountLimit != null
                        ? `${residentInfo.mealCountUsage ?? 0} meals`
                        : `${residentInfo.mealCountUsage ?? 0} meals`,
                    primaryLabel: "Used",
                    secondaryValue:
                      mealCountPlan.mealCountLimit != null
                        ? mealCountPlan.mealCountLimit - (residentInfo.mealCountUsage ?? 0)
                        : "No Limit",
                    secondaryLabel: mealCountPlan.mealCountLimit != null ? "Remaining" : "",
                    isSecondaryNegative:
                      mealCountPlan.mealCountLimit != null &&
                      mealCountPlan.mealCountLimit - (residentInfo.mealCountUsage ?? 0) <= 0,
                  })}
                {mealCountPlan && mealCostPlan && (
                  <Box sx={{ borderTop: "1px solid lightgray" }} />
                )}
                {mealCostPlan &&
                  (() => {
                    // rollover plans carry a running balance across periods; non-rollover plans reset each period, so remaining is just limit minus usage
                    const remaining = mealCostPlan.isAllowedRollOver
                      ? (mealCostPlan.remainingCostBalance ?? 0)
                      : mealCostPlan.mealCostLimit - (residentInfo.mealCostUsage ?? 0);
                    return renderMealPlanRow({
                      title: "Meal Cost",
                      subtitle: getMealPlanTypes(mealCostPlan),
                      primaryValue: `$${(residentInfo.mealCostUsage ?? 0).toFixed(2)}`,
                      primaryLabel: mealCostPlan.mealCostLimit != null ? "Used" : "Balance",
                      secondaryValue:
                        mealCostPlan.mealCostLimit != null
                          ? `$${remaining.toFixed(2)}`
                          : "No Limit",
                      secondaryLabel: mealCostPlan.mealCostLimit != null ? "Remaining" : "",
                      isSecondaryNegative: mealCostPlan.mealCostLimit != null && remaining <= 0,
                    });
                  })()}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
}